import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { reportsService } from '../services/api';
import { formatMoney } from '../utils/pricing';
import { PERIODS, fmtDate, fmtNum, monthLabel } from '../utils/businessCosts';
import './Reports.css';

const money = (v) => (Number(v) < 0 ? `-${formatMoney(Math.abs(Number(v)))}` : formatMoney(v));
const sign = (v) => (v === null || v === undefined ? '' : v < 0 ? 'neg' : v > 0 ? 'pos' : '');

/**
 * Reports (backlog #681 part 2): the management view. P&L by month — a job
 * counts in the month it was done and its costs follow it — the business
 * costs that actually fell in the period against the overheads allocated,
 * direct costs by category, the jobs behind the numbers, and how hard each
 * piece of equipment is working. Internal only.
 */
export default function Reports() {
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get('month');
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const monthRange = (ym) => { const [y, m] = ym.split('-').map(Number); const last = new Date(Date.UTC(y, m, 0)).getUTCDate(); return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` }; };
  const [preset, setPreset] = useState(() => (/^\d{4}-\d{2}$/.test(monthParam || '') || (fromParam && toParam)) ? 'custom' : 'this_fy');
  const [custom, setCustom] = useState(() => (/^\d{4}-\d{2}$/.test(monthParam || '') ? monthRange(monthParam) : (fromParam && toParam ? { from: fromParam, to: toParam } : { from: '', to: '' })));
  const [selectedMonth, setSelectedMonth] = useState(() => (/^\d{4}-\d{2}$/.test(monthParam || '') ? monthParam : null));
  const [pl, setPl] = useState(null);
  const [util, setUtil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const params = preset === 'custom' ? (custom.from && custom.to && custom.to >= custom.from ? { from: custom.from, to: custom.to } : null) : { preset };

  useEffect(() => {
    if (!params) return undefined;
    let alive = true;
    setLoading(true);
    Promise.all([reportsService.pl(params), reportsService.utilisation(params)]).then(([p, u]) => {
      if (!alive) return;
      if (p.success) { setPl(p.report); setError(''); } else setError(p.error || 'Could not build the report');
      if (u.success) setUtil(u.equipment || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [preset, custom.from, custom.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const r = pl;
  const rd = r?.recovery_detail; // recovery compared like with like — to today when the period runs past it (Bug #397)
  const bc = rd?.costs;
  const paceWords = (p, capped) => (p ? (p.basis === 'hours'
    ? `${fmtNum(p.actual, 1)} h booked · ~${fmtNum(p.expected, 0)} h planned ${capped ? 'by now' : 'for the period'}`
    : `${formatMoney(p.actual)} revenue · ~${formatMoney(p.expected)} expected ${capped ? 'by now' : 'for the period'}`) : '');
  const recoveryWords = (d) => {
    if (d.recovery >= 0) return 'the jobs carried more overheads than the business actually spent';
    const p = d.pace;
    if (d.reason === 'behind_pace') return p.basis === 'hours'
      ? `only ${fmtNum(p.actual, 1)} of the ~${fmtNum(p.expected, 0)} hours planned by this point have been booked — overheads come in as the hours are worked`
      : `only ${formatMoney(p.actual)} of the ~${formatMoney(p.expected)} revenue expected by this point has come in — overheads come in with the work`;
    if (d.reason === 'rate_low') return p.basis === 'hours'
      ? 'the hours are on pace, yet the jobs carried less than the business spent — the rate is set too low'
      : 'the revenue is on pace, yet the jobs carried less than the business spent — the rate is set too low';
    return 'the business spent more than the jobs carried — set the planned hours or expected revenue on Business costs to see why';
  };
  const rateText = r ? (r.rate.basis === 'hours'
    ? (r.rate.rate_per_hour === null ? 'no overhead rate yet' : `${formatMoney(r.rate.rate_per_hour)} per hour`)
    : (r.rate.pct_of_revenue === null ? 'no overhead rate yet' : `${r.rate.pct_of_revenue}% of revenue`)) : '';
  const jobsShown = r ? (selectedMonth ? r.jobs.filter((j) => j.done_on.startsWith(selectedMonth)) : r.jobs) : [];

  return (
    <div className="rp-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-muted rp-intro">What the business made, month by month. A job counts in the month it was done and its costs follow it. Internal only — management figures, not tax.</p>
        </div>
      </div>

      <div className="rp-periods">
        {PERIODS.map((p) => (
          <button key={p.value} type="button" className={`rp-chip ${preset === p.value ? 'active' : ''}`} onClick={() => { setPreset(p.value); setSelectedMonth(null); }}>{p.label}</button>
        ))}
        <button type="button" className={`rp-chip ${preset === 'custom' ? 'active' : ''}`} onClick={() => { setPreset('custom'); setSelectedMonth(null); }}>Custom</button>
        {preset === 'custom' && (
          <span className="rp-custom">
            <input type="date" aria-label="From" value={custom.from} onChange={(e) => { setCustom((c) => ({ ...c, from: e.target.value })); setSelectedMonth(null); }} />
            <span className="text-muted">→</span>
            <input type="date" aria-label="To" value={custom.to} onChange={(e) => { setCustom((c) => ({ ...c, to: e.target.value })); setSelectedMonth(null); }} />
          </span>
        )}
        {r && <span className="text-muted rp-range">{fmtDate(r.from)} → {fmtDate(r.to)}</span>}
      </div>
      {error && <p className="form-error">{error}</p>}
      {loading && !r && <div className="empty-state"><div className="loading-spinner"></div></div>}

      {r && (
        <>
          {/* ---- P&L by month ---- */}
          <section className={`card rp-section ${loading ? 'rp-stale' : ''}`}>
            <div className="rp-head">
              <h2 className="card-title">Profit and loss</h2>
              <div className="rp-head-right">
                <span className="text-muted rp-note">Overheads at {rateText}{r.rate.pool_mode === 'budget' ? ' (budget)' : ''} · <Link to="/business-costs">Business costs</Link></span>
                {params && (
                  <span className="rp-csv-links">
                    <a className="btn btn-sm btn-outline" href={reportsService.plCsvUrl(params)} download>Download P&L CSV</a>
                    <a className="btn btn-sm btn-outline" href={reportsService.costsCsvUrl(params)} download>Download costs CSV</a>
                  </span>
                )}
              </div>
            </div>
            {r.months.length === 0 ? (
              <p className="text-muted rp-hint">No jobs done in this period. Booked, in-progress, complete and invoiced jobs count — enquiries and unbooked quotes do not.</p>
            ) : (
              <div className="rp-table-wrap">
                <table className="rp-table rp-table-months">
                  <thead><tr><th>Month</th><th className="num">Jobs</th><th className="num">Hours</th><th className="num">Revenue</th><th className="num">Direct costs</th><th className="num">Overheads</th><th className="num">Profit</th><th className="num">%</th></tr></thead>
                  <tbody>
                    {r.months.map((m) => (
                      <tr key={m.month} className={selectedMonth === m.month ? 'rp-row-active' : ''} role="button" tabIndex={0} aria-pressed={selectedMonth === m.month}
                        onClick={() => setSelectedMonth(selectedMonth === m.month ? null : m.month)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedMonth(selectedMonth === m.month ? null : m.month); } }}>
                        <td>{monthLabel(m.month)}</td>
                        <td className="num">{m.jobs}{m.unpriced ? <em className="text-muted" title="Jobs with nothing quoted or priced yet"> ({m.unpriced} unpriced)</em> : null}</td>
                        <td className="num">{fmtNum(m.hours, 1)}</td>
                        <td className="num">{formatMoney(m.revenue)}</td>
                        <td className="num">{formatMoney(m.direct_costs)}</td>
                        <td className="num">{formatMoney(m.overheads)}</td>
                        <td className={`num ${sign(m.profit)}`}><strong>{money(m.profit)}</strong></td>
                        <td className="num">{m.profit_pct === null ? '—' : `${m.profit_pct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="num">{r.totals.jobs}</td>
                      <td className="num">{fmtNum(r.totals.hours, 1)}</td>
                      <td className="num">{formatMoney(r.totals.revenue)}</td>
                      <td className="num">{formatMoney(r.totals.direct_costs)}</td>
                      <td className="num">{formatMoney(r.totals.overheads)}</td>
                      <td className={`num ${sign(r.totals.profit)}`}><strong>{money(r.totals.profit)}</strong></td>
                      <td className="num">{r.totals.profit_pct === null ? '—' : `${r.totals.profit_pct}%`}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {rd && bc && (
              <div className="rp-recovery">
                <div className="rp-recovery-grid">
                  <span className="rp-pill"><em>{rd.capped ? 'Overheads carried by jobs so far' : 'Overheads allocated to jobs'}</em><strong>{formatMoney(rd.allocated)}</strong>
                    {rd.pace && <small className="text-muted">{paceWords(rd.pace, rd.capped)}</small>}</span>
                  <span className="rp-pill"><em>{rd.capped ? `Business costs so far (to ${fmtDate(rd.to)})` : 'Business costs in the period'}</em><strong>{formatMoney(bc.total)}</strong>
                    <small className="text-muted">{formatMoney(bc.expenses_one_off)} one-off · {formatMoney(bc.expenses_recurring)} bills ({bc.instalments}) · {formatMoney(bc.depreciation)} wear · {formatMoney(bc.loan_interest)} interest</small></span>
                  <span className={`rp-pill ${sign(rd.recovery)}`}><em>{rd.recovery >= 0 ? 'Over-recovered' : 'Under-recovered'}{rd.capped ? ' so far' : ''}</em><strong>{money(Math.abs(rd.recovery))}</strong>
                    <small className="text-muted">{rd.pct !== null ? `${fmtNum(rd.pct, 0)}% recovered · ` : ''}{recoveryWords(rd)}</small></span>
                </div>
              </div>
            )}
          </section>

          <div className="rp-grid">
            {/* ---- Direct costs by category ---- */}
            <section className={`card rp-section ${loading ? 'rp-stale' : ''}`}>
              <h2 className="card-title mb-md">Direct costs by category</h2>
              {r.by_category.length === 0 ? <p className="text-muted rp-hint">No cost lines on the jobs in this period.</p> : (
                <table className="rp-table rp-table-sm">
                  <tbody>
                    {r.by_category.map((c) => (
                      <tr key={c.name}><td>{c.name}<em className="text-muted"> · {c.lines} line{c.lines === 1 ? '' : 's'}</em></td><td className="num">{formatMoney(c.amount)}</td><td className="num text-muted">{c.pct === null ? '' : `${c.pct}%`}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* ---- Equipment utilisation ---- */}
            <section className={`card rp-section ${loading ? 'rp-stale' : ''}`}>
              <h2 className="card-title mb-md">Equipment</h2>
              {!util || util.length === 0 ? <p className="text-muted rp-hint">Nothing on the register yet — add it under Business costs.</p> : (
                <table className="rp-table rp-table-sm">
                  <thead><tr><th>Asset</th><th className="num">Hours in period</th><th className="num">All time</th><th className="num">Life used</th><th>Left</th></tr></thead>
                  <tbody>
                    {util.map((e) => (
                      <tr key={e.id} className={e.is_active ? '' : 'rp-dim'}>
                        <td>{e.name}{!e.is_active ? <em className="text-muted"> · retired</em> : ''}<em className="text-muted"> · {e.hourly_rate !== null ? `${formatMoney(e.hourly_rate)}/h` : e.yearly_depreciation !== null ? `${formatMoney(e.yearly_depreciation)}/yr` : ''}</em></td>
                        <td className="num">{e.hourly_rate !== null ? `${fmtNum(e.hours_in_period, 1)} h` : '—'}</td>
                        <td className="num">{e.hourly_rate !== null ? `${fmtNum(e.hours_all, 1)} h` : '—'}</td>
                        <td className="num">{e.life_used_pct !== null ? `${fmtNum(e.life_used_pct, 0)}%` : e.life_end ? (e.depreciating ? `to ${fmtDate(e.life_end)}` : 'done') : '—'}</td>
                        <td>{e.hourly_rate !== null ? (e.months_left !== null ? `~${e.months_left} month${e.months_left === 1 ? '' : 's'} at this pace` : `${fmtNum(e.hours_remaining, 0)} h`) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          {/* ---- The jobs behind the numbers ---- */}
          <section className={`card rp-section ${loading ? 'rp-stale' : ''}`}>
            <div className="rp-head">
              <h2 className="card-title mb-md">{selectedMonth ? `Jobs in ${monthLabel(selectedMonth)} (${jobsShown.length})` : `Jobs in this period (${r.jobs.length})`}</h2>
              {selectedMonth && <button type="button" className="btn-link" onClick={() => setSelectedMonth(null)}>Show all months</button>}
            </div>
            {jobsShown.length === 0 ? <p className="text-muted rp-hint">None.</p> : (
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead><tr><th>Done</th><th>Job</th><th>Client</th><th className="num">Hours</th><th className="num">Revenue</th><th className="num">Direct costs</th><th className="num">Overheads</th><th className="num">Profit</th></tr></thead>
                  <tbody>
                    {jobsShown.map((j) => (
                      <tr key={j.id}>
                        <td className="nowrap">{fmtDate(j.done_on)}</td>
                        <td><Link to={`/jobs/${j.id}`}>{j.job_number}</Link>{j.title ? ` · ${j.title}` : ''}</td>
                        <td>{j.customer_name || '—'}</td>
                        <td className="num">{fmtNum(j.hours, 1)}</td>
                        <td className="num">{j.revenue === null ? <em className="text-muted">unpriced</em> : formatMoney(j.revenue)}</td>
                        <td className="num">{formatMoney(j.direct_costs)}</td>
                        <td className="num">{j.overheads === null ? '—' : formatMoney(j.overheads)}</td>
                        <td className={`num ${sign(j.profit)}`}>{j.profit === null ? '—' : money(j.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
